<template>
  <div>
    <DxPopup
      :visible="popupVisible"
      :drag-enabled="true"
      :hide-on-outside-click="false"
      :show-title="true"
      :width="'75%'"
      :height="'75%'"
      :title="getTitle"
      :ref="'add-item-popup'"
      @hidden="popupHidden"
      @shown="popupShown"
      :showCloseButton="false"
      :wrapperAttr="{
        class: 'css-v2 gc-popup gc-popup-deposit-claim',
        id: 'gc-popup-deposit-claim',
      }"
    >
      <div class="css-v2 gc-popup-form">
        <gc-form
          :formOptions="{
            gcLabelLocation: 'left',
          }"
          :formItems="getHeaderFormItems"
          :formHeight="95"
          :isNormal="true"
          :data-model="getHeaderFormDataModel"
          :formMode="'create'"
          :validationGroup="'gc-popup-deposit-claim-header-form'"
          :formTitle="'gc-popup'"
          :ref="'gc-popup-deposit-claim-header-form'"
        ></gc-form>
      </div>

      <div class="details-data-grid">
        <dx-data-grid
          :columns="getColumns"
          :data-source="getDataSource"
          :columnChooser="{
            enabled: false,
          }"
          :searchPanel="{
            visible: false,
          }"
          ref="dx-data-grid-comp"
          :pager="{ visible: false }"
          :height="getGridHeight"
          @row-expanded="onRowExpanded"
          @row-collapsed="onRowCollapsed"
          @cell-prepared="onCellPrepared"
          @editor-preparing="onEditorPreparing"
          :row-alternation-enabled="true"
          :selection="{
            mode: 'none',
          }"
        >
          <DxEditing
            :allow-updating="true"
            :allow-deleting="true"
            mode="cell"
            :new-row-position="'last'"
            :use-icons="true"
            :changes="changes"
            :select-text-on-edit-start="true"
            :refreshMode="'reshape'"
          />
          <template #Decimal="{ data }">
            <GcDecimal :dataValue="data" :format="data.column.format" />
          </template>

          <DxMasterDetail :enabled="true" template="masterDetailTemplate" />
          <template #masterDetailTemplate="{ data: masterData }">
            <div class="css-v2 gc-popup-form master-details-form">
              <form>
                <DxForm
                  :form-data="masterData.data"
                  :show-colon-after-label="false"
                  :show-required-mark="false"
                  :show-validation-summary="false"
                  :scrolling-enabled="false"
                  :height="'auto'"
                  :validation-group="
                    `gc-popup-deposit-claim-header-form-${masterData.data.ID}`
                  "
                  :col-count-by-screen="{
                    xs: 1,
                    sm: 1,
                    md: 1,
                    lg: 1,
                  }"
                  :label-location="'left'"
                  :items.sync="getMasterDetailFormItems"
                  @content-ready="
                    onContentReadyDxMasterDetail(
                      $event,
                      `gc-popup-deposit-claim-header-form-${masterData.data.ID}`
                    )
                  "
                >
                  <template #item-template="{ data }">
                    <div class="custom-auto-complete">
                      <span class="auto-complete-primary">{{
                        getPrimaryText(data)
                      }}</span>
                      <span class="auto-complete-secondary">
                        {{ getSecondaryText(data) }}
                      </span>
                    </div>
                  </template>

                  <template #FXPaymentAmount="{ data }">
                    <div>
                      <DxNumberBox
                        :value="masterData.data[data.dataField]"
                        :format="data.editorOptions.format"
                        :min="0"
                        :max="
                          masterData.data.FxClaimAmount +
                            masterData.data.TaxFXLineTotal
                        "
                        @value-changed="
                          onFXPaymentAmountValueChanged($event, masterData)
                        "
                      />
                    </div>
                  </template>
                  <template #PaymentToggle="{ data }">
                    <div>
                      <DxSwitch
                        :value="masterData.data[data.dataField]"
                        @value-changed="
                          onPaymentToggleValueChanged($event, masterData)
                        "
                      />
                    </div>
                  </template>

                  <template #EditFeeModel>
                    <div class="edit-fee-template">
                      <span @click="showBankFeeSettingsModal(masterData)"
                        >Edit</span
                      >
                    </div>
                  </template>
                </DxForm>
              </form>
            </div>
          </template>
        </dx-data-grid>
      </div>

      <div class="footer">
        <DxButton
          :text="'Save'"
          type="success"
          styling-mode="contained"
          @click="saveDepositClaim"
        />
        <DxButton
          :text="'Cancel'"
          type="default"
          styling-mode="contained"
          @click="popupHidden"
        />
      </div>
    </DxPopup>

    <DxLoadPanel
      :position="{ of: '#container-wrapper' }"
      :visible.sync="loadingVisible"
      :show-indicator="true"
      :show-pane="true"
      :shading="true"
      :hide-on-outside-click="false"
      shading-color="rgba(0,0,0,0)"
    />

    <GcPopupBankFee
      v-if="gcPopupBankFeeConfig.visible"
      :visible="gcPopupBankFeeConfig.visible"
      :callback="gcPopupBankFeeConfig.callback"
      :bankFeeSettings="gcPopupBankFeeConfig.bankFeeSettings"
      :setLoading="setLoading"
    />
    <GcPopupValidationSummary
      v-if="formsConfig"
      :showPopup.sync="formsConfig.validationSummaryVisible"
      :title="'Validation Errors'"
      :isXSmall="isXSmall"
      :items.sync="formsConfig.items"
      :onHidden="onHiddenGcPopupValidationSummary"
    ></GcPopupValidationSummary>
  </div>
</template>

<script>
import ArrayStore from "devextreme/data/array_store";
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import DataGridMixin from "@/_mixins/data-grid.mixin";
import { DepositClaimService } from "../_services/api/sales/deposit-claim.service";
import { ForeignCurrencyService } from "../_services/api/settings/accounting/foreign-currency.service";
import ColumnWidth from "../_helpers/data-grid/ColumnWidth";
import { getFactorByCode, getGlobalField } from "../_store/helpers";
import { BankFeeSettings } from "../_services/api/finance/bank-fee-settings.service";
import validationEngine from "devextreme/ui/validation_engine";
import { CustomerService } from "../_services/api/customers/customer.service";
import { TaxCategoryService } from "../_services/api/settings/accounting/tax-category.service";
import salesFormulas from "../_helpers/formulas/sales-formulas";
import { BankAccountTransactionServices } from "../_services/api/finance/bank-account-transaction.service";
import { CompanyService } from "../_services/api/accounts/company.service";

const rowBase = {
  ID: 1,
  ClaimDate: new Date(),
  DueDate: null,
  Invoice: null,
  ClaimPrcnt: 0,
  FxClaimAmount: 0,
  Comment: null,
  IsNew: true,
  BankFeeBranchCode: null,
  BankFeeDivisionCode: null,
  BankFeeComment: null,
  BankFeeGLAccountCode: 0,
  PaymentMethod: null,
  BankAccount: null,
  ReferenceNumber: null,
  PaymentToggle: false,
  FXPaymentAmount: 0,
  FXAmount: 0,
  DateSettled: null,
  TaxFXLineTotal: 0,
};

const { calculateWetFXLineTotal, calculateTaxFXLineTotal } = salesFormulas;

export default {
  props: {
    setVisible: Boolean,
    callBack: {
      type: Function,
      default: function() {},
    },
    salesOrderData: Object,
    setLoading: {
      type: Function,
      default: function() {},
    },
  },
  mixins: [RouteMixin, FormMixin, DataGridMixin],
  data() {
    return {
      headerFormModel: {
        DateIn: undefined,
        CustomerName: undefined,
        CustomerCode: undefined,
        Payer: undefined,
        CurrencyCode: undefined,
        CurrencyName: undefined,
        ExchangeRate: undefined,
        OrderAmount: 0,
        OrderBalance: 0,
        ClaimTotal: 0,
      },
      DefaultPaymentMethod: "",
      localDataSource: new ArrayStore({
        key: "ID",
        data: [],
      }),
      changes: [],
      bankFeePopupVisible: false,
      bankFeeSettingsModel: {
        GLAccount: undefined,
        Branch: undefined,
        Division: undefined,
        Comment: undefined,
      },
      popupContentHeight: undefined,
      TermDueDate: undefined,
      dataModel: {
        AccountingPeriodCode: undefined,
      },
      PaymentMaxDate: undefined,
      PaymentMinDate: undefined,
      loadingVisible: false,
      loading: true,
      taxModel: {
        TaxCode: undefined,
        Rate: undefined,
        WetRate: undefined,
      },
      BaseCurrency: undefined,
    };
  },
  async created() {
    document.addEventListener("keyup", this.closeOnEscape);
    await this.$store.dispatch("screenFactors/getFactors", [
      237,
      540,
      666,
      667,
      668,
    ]);
    this.initialized();
  },
  computed: {
    popupVisible() {
      return this.setVisible ? true : false;
    },
    getTitle() {
      return `Deposit Claim for Sales Order #${this.salesOrderData.Code}`;
    },
    getHeaderFormDataModel() {
      return this.loading ? {} : this.headerFormModel;
    },
    getHeaderFormItems() {
      return [
        {
          itemType: "group",
          caption: "",
          colCount: 3,
          items: [
            {
              itemType: "group",
              caption: "",
              colCount: 1,
              items: [
                {
                  screenField: "DateIn",
                  editorOptions: {
                    allowUpdate: false,
                  },
                },
                {
                  screenField: "CustomerName",
                  dataField: "CustomerName",
                  label: {
                    text: "Customer",
                  },
                  editorOptions: {
                    allowUpdate: false,
                  },
                },
                {
                  screenField: "Payer",
                  allowUpdate: true,
                },
              ],
            },
            {
              itemType: "group",
              caption: "",
              colCount: 1,
              items: [
                {
                  screenField: "CurrencyCode",
                  dataField: "CurrencyName",
                  editorOptions: {
                    allowUpdate: false,
                  },
                },
                {
                  screenField: "ExchangeRate",
                  editorOptions: {
                    allowUpdate:
                      this.BaseCurrency != this.headerFormModel.CurrencyCode,
                  },
                },
              ],
            },
            {
              itemType: "group",
              caption: "",
              colCount: 1,
              items: [
                {
                  screenField: "FXAmount",
                  dataField: "OrderAmount",
                  label: {
                    text: `Order Amount (${this.headerFormModel.CurrencyCode})`,
                  },
                  editorOptions: {
                    allowUpdate: false,
                  },
                },
                {
                  screenField: "FXAmount",
                  dataField: "OrderBalance",
                  label: {
                    text: `Order Balance (${this.headerFormModel.CurrencyCode})`,
                  },
                  editorOptions: {
                    allowUpdate: false,
                  },
                },
                {
                  screenField: "FXAmount",
                  dataField: "ClaimTotal",
                  label: {
                    text: `Claim Total (${this.headerFormModel.CurrencyCode})`,
                  },
                  editorOptions: {
                    allowUpdate: false,
                  },
                },
              ],
            },
          ],
        },
      ];
    },
    getMasterDetailFormItems() {
      return this.parseFormItems([
        {
          itemType: "group",
          caption: "",
          colCount: 1,
          items: [
            {
              itemType: "group",
              caption: "",
              colCount: 2,
              items: [
                {
                  label: {
                    text: "Add Payment",
                  },
                  required: false,
                  dataField: "title",
                  cssClass: "form-field-as-header",
                },
                {
                  screenField: "Inactive",
                  dataField: "PaymentToggle",
                  label: {
                    text: " ",
                  },
                  cssClass: "payment-toggle",
                  template: "PaymentToggle",
                },
              ],
            },
            {
              itemType: "group",
              caption: "",
              colCount: 3,
              items: [
                {
                  screenField: "PaymentMethod",
                  dataField: "PaymentMethod",
                  required: true,
                  editorOptions: {
                    allowUpdate: true,
                  },
                },
                {
                  screenField: "FXAmount",
                  dataField: "FXPaymentAmount",
                  label: {
                    text: `Payment (${this.headerFormModel.CurrencyCode})`,
                  },
                  template: "FXPaymentAmount",
                  editorOptions: {
                    allowUpdate: true,
                    editorTemplate: "FXPaymentAmount",
                  },
                },
              ],
            },
            {
              itemType: "group",
              caption: "",
              colCount: 3,
              items: [
                {
                  screenField: "ReferenceNumber",
                  dataField: "ReferenceNumber",
                },
                {
                  screenField: "FXAmount",
                  dataField: "FXAmount",
                  label: {
                    text: `Fee (${this.headerFormModel.CurrencyCode})`,
                  },
                  editorOptions: {
                    allowUpdate: true,
                  },
                  required: true,
                },
                {
                  screenField: "",
                  dataField: "edit",
                  template: "EditFeeModel",
                  cssClass: "edit-fee",
                  label: {
                    visible: false,
                  },
                },
              ],
            },
            {
              itemType: "group",
              caption: "",
              colCount: 3,
              items: [
                {
                  screenField: "BankAccount",
                  dataField: "BankAccount",
                  editorOptions: {
                    allowUpdate: true,
                  },
                  required: true,
                  filter:
                    this.BaseCurrency == this.headerFormModel.CurrencyCode
                      ? [["CurrencyCode", "=", this.BaseCurrency]]
                      : [
                          ["CurrencyCode", "=", this.BaseCurrency],
                          "or",
                          [
                            "CurrencyCode",
                            "=",
                            this.headerFormModel.CurrencyCode,
                          ],
                        ],
                },
              ],
            },
          ],
        },
      ]);
    },
    getColumns() {
      return this.parseGridColumns([
        {
          screenField: "DateIn",
          caption: "Claim Date",
          dataField: "ClaimDate",
          allowEditing: true,
          editorType: "dxDateBox",
          maxWidth: ColumnWidth.Date.width,
          editorOptions: {
            min: this.PaymentMinDate,
            max: this.PaymentMaxDate,
            onFocusIn: (e) => {
              this.removeCellFocus();

              e.event.target.select();
              e.element.parentElement.classList.add("key-pad-focused-cell");
            },
          },
          cssClass: "gc-editable-cell-color",
        },
        {
          screenField: "DueDate",
          caption: "Due Date",
          dataField: "DueDate",
          allowEditing: true,
          editorType: "dxDateBox",
          maxWidth: ColumnWidth.Date.width,
          cssClass: "gc-editable-cell-color",
          editorOptions: {
            onFocusIn: (e) => {
              this.removeCellFocus();

              e.event.target.select();
              e.element.parentElement.classList.add("key-pad-focused-cell");
            },
          },
        },
        {
          screenField: "Invoice",
          caption: "Invoice",
          dataField: "Invoice",
          maxWidth: ColumnWidth.InvoiceNumber.width,
        },
        {
          screenField: "DiscountPercent",
          caption: "Claim %",
          dataField: "ClaimPrcnt",
          allowEditing: true,
          editorType: "dxNumberBox",
          maxWidth: ColumnWidth.Percentage.width,
          setCellValue: (newData, value, rowData) => {
            newData.ClaimPrcnt = value;
            newData.FxClaimAmount =
              this.headerFormModel.OrderAmount * (value / 100);

            var WetFXLineTotal = calculateWetFXLineTotal(
              newData.FxClaimAmount,
              this.taxModel.WetRate
            );

            newData.TaxFXLineTotal = calculateTaxFXLineTotal(
              newData.FxClaimAmount,
              WetFXLineTotal,
              this.taxModel.Rate
            );
            if (rowData.PaymentToggle)
              newData.FXPaymentAmount =
                newData.FxClaimAmount + newData.TaxFXLineTotal;

            return newData;
          },
          editorOptions: {
            onFocusIn: (e) => {
              this.removeCellFocus();

              e.event.target.select();
              e.element.parentElement.classList.add("key-pad-focused-cell");
            },
          },
          cssClass: "gc-editable-cell-color",
        },
        {
          screenField: "Allocated",
          caption: `Claim (${this.headerFormModel.CurrencyCode})`,
          dataField: "FxClaimAmount",
          allowEditing: true,
          editorType: "dxNumberBox",
          format: "Decimal-16-2C",
          maxWidth: ColumnWidth.Price2DecimalPoints.width,
          setCellValue: (newData, value, rowData) => {
            newData.FxClaimAmount = value;
            newData.ClaimPrcnt = (
              (value / this.headerFormModel.OrderAmount) *
              100
            ).roundNumber(2);

            var WetFXLineTotal = calculateWetFXLineTotal(
              newData.FxClaimAmount,
              this.taxModel.WetRate
            );

            newData.TaxFXLineTotal = calculateTaxFXLineTotal(
              newData.FxClaimAmount,
              WetFXLineTotal,
              this.taxModel.Rate
            );
            if (rowData.PaymentToggle)
              newData.FXPaymentAmount = value + newData.TaxFXLineTotal;

            return newData;
          },
          editorOptions: {
            onFocusIn: (e) => {
              this.removeCellFocus();

              e.event.target.select();
              e.element.parentElement.classList.add("key-pad-focused-cell");
            },
          },
          cssClass: "gc-editable-cell-color",
        },
        {
          screenField: "TaxFXTotal",
          caption: `Tax (${this.headerFormModel.CurrencyCode})`,
          dataField: "TaxFXLineTotal",
          format: "Decimal-16-2C",
          allowEditing: false,
          allowSorting: false,
          allowSearch: false,
          maxWidth: ColumnWidth.TaxFXLineTotal.width,
        },
        {
          screenField: "FXAmount",
          dataField: "FXPaymentAmount",
          caption: `Payment (${this.headerFormModel.CurrencyCode})`,
          format: "Decimal-16-2C",
          readOnly: true,
          calculateCellValue: (e) => {
            if (e.PaymentToggle || !e.IsNew) {
              return e.FXPaymentAmount;
            } else {
              return 0;
            }
          },
          maxWidth: ColumnWidth.Price4DecimalPoints.width,
        },
        {
          screenField: "Comment",
          caption: "Comment",
          dataField: "Comment",
          editorType: "dxTextArea",
          allowEditing: true,

          editorOptions: {
            maxLength: 100,
            onFocusIn: (e) => {
              this.removeCellFocus();

              e.event.target.select();
              e.element.parentElement.classList.add("key-pad-focused-cell");
            },
          },
          cssClass: "gc-editable-cell-color",
        },
        {
          caption: "",
          dataField: "",
          type: "buttons",
          alignment: "right",
          maxWidth: 90,
          buttons: [
            {
              hint: "Add Payment",
              icon: "fa-solid fa-money-check-dollar",
              cssClass: "add-payment-button",
              onClick: (e) => {
                var dataGrid = this.$refs["dx-data-grid-comp"].instance;
                if (!e.row.isExpanded) {
                  dataGrid.expandRow(e.row.data);
                } else {
                  dataGrid.collapseRow(e.row.data);
                }
              },
              visible: (e) => {
                return e.row.data.IsNew;
              },
            },
            {
              hint: "Delete",
              icon: "trash",
              onClick: async (e) => {
                if (e.row.data.DateSettled != null) return;
                var row = this.getNewRowData();
                row.ID = e.row.data.ID;
                this.localDataSource.update(row.ID, row);
              },
              visible: (e) => {
                return !e.row.data.Invoice;
              },
            },
            {
              hint: "Add",
              icon: "plus",
              onClick: async (e) => {
                this.addNewRow(e);
              },
              visible: (e) => {
                return !e.row.data.Invoice;
              },
            },
          ],
        },
      ]);
    },
    getDataSource() {
      return this.localDataSource._array;
    },
    getGridHeight() {
      return this.popupContentHeight
        ? this.popupContentHeight - 40 - 95 - 50 - 40
        : "auto";
      // 40 title height
      // 95 header form heigh
      // 50 extra paddings
      // footer height
    },
    gcPopupBankFeeConfig() {
      return {
        visible: this.bankFeePopupVisible,
        callback: this.bankFeeCallback,
        bankFeeSettings: {
          GLAccount: this.bankFeeSettingsModel.BankFeeGLAccountCode,
          Branch: this.bankFeeSettingsModel.BankFeeBranchCode,
          Division: this.bankFeeSettingsModel.BankFeeDivisionCode,
          Comment: this.bankFeeSettingsModel.BankFeeComment,
          Key: this.bankFeeSettingsModel.ID,
        },
      };
    },
  },
  destroyed() {
    document.removeEventListener("keyup", this.closeOnEscape);
  },
  methods: {
    onFXPaymentAmountValueChanged(e, masterData) {
      if (!e.event) return;
      this.localDataSource.update(masterData.data.ID, {
        PaymentToggle: true,
        FXPaymentAmount: e.value,
      });
    },
    onPaymentToggleValueChanged(e, masterData) {
      if (!e.event) return;
      var obj = {
        PaymentToggle: e.value,
      };
      if (e.value == false) {
        obj.FXPaymentAmount = 0;
      } else {
        obj.FXPaymentAmount =
          masterData.data.FxClaimAmount + masterData.data.TaxFXLineTotal;
      }
      this.localDataSource.update(masterData.data.ID, obj);
    },
    totalClaimAmount() {
      let claimTotal = 0;
      this.localDataSource._array.forEach((element) => {
        claimTotal += element.FxClaimAmount;
      });
      return claimTotal;
    },
    totalOrderAmount() {
      return this.headerFormModel.OrderAmount;
    },
    closeOnEscape(e) {
      if (e.code == "Escape") {
        if (!this.bankFeePopupVisible) {
          this.popupHidden();
        }
      }
    },
    showPopupLoading(show) {
      this.loadingVisible = show;
    },
    showBankFeeSettingsModal(masterData) {
      this.bankFeeSettingsModel = masterData.data;
      this.bankFeePopupVisible = true;
    },
    bankFeeCallback(cancel, newBankFeeSettings) {
      if (cancel) {
        this.bankFeePopupVisible = false;
      } else if (!cancel && newBankFeeSettings) {
        this.bankFeePopupVisible = false;

        this.localDataSource.update(newBankFeeSettings.Key, {
          BankFeeGLAccountCode: newBankFeeSettings.GLAccount,
          BankFeeBranchCode: newBankFeeSettings.Branch,
          BankFeeDivisionCode: newBankFeeSettings.Division,
          BankFeeComment: newBankFeeSettings.Comment,
        });
      }
    },
    async initialized() {
      this.showPopupLoading(true);

      this.headerFormModel.DateIn = this.salesOrderData.DateIn;
      this.headerFormModel.CustomerCode = this.salesOrderData.CustomerCode;
      this.headerFormModel.CustomerName = this.salesOrderData.CustomerName;
      this.headerFormModel.Payer = this.salesOrderData.CustomerName;
      this.headerFormModel.CurrencyCode = this.salesOrderData.CurrencyCode;

      await CustomerService.getByCode(
        this.headerFormModel.CustomerCode,
        "Code,Gst"
      ).then(async (res) => {
        if (res.data.value.length > 0) {
          if (res.data.value[0].Gst.toLowerCase() == "yes")
            this.taxModel.TaxCode = "G";
          else this.taxModel.TaxCode = "E";
          await TaxCategoryService.getByCode(this.taxModel.TaxCode).then(
            (tax) => {
              if (tax.data.value.length > 0) {
                this.taxModel.Rate = tax.data.value[0].Rate;
                this.taxModel.WetRate = tax.data.value[0].WetRate;
              }
            }
          );
        }
      });

      let currency = await ForeignCurrencyService.getByCode(
        this.headerFormModel.CurrencyCode
      );

      this.headerFormModel.CurrencyName = currency.data.value[0].Description;
      this.headerFormModel.ExchangeRate = currency.data.value[0].BuyRate;
      CompanyService.getCompany().then(async (res) => {
        let companyDetails = res.data.value[0];
        if (companyDetails) {
          this.BaseCurrency = companyDetails.CurrencyCode;
        } else {
          this.BaseCurrency = "AUD";
        }
      });
      await DepositClaimService.getActiveAccountingPeriodStartDate().then(
        (startDateResult) => {
          if (startDateResult) {
            let accountingPeriodStartDate = new Date(
              startDateResult.data.value[0].StartDate.replace("Z", "")
            );
            this.PaymentMinDate = accountingPeriodStartDate;
          }
        }
      );

      let endDateResult = await DepositClaimService.getActiveAccountingPeriodEndDate();
      if (endDateResult) {
        let accountingPeriodEndDate = endDateResult.data.value[0].EndDate;
        this.PaymentMaxDate = new Date(
          accountingPeriodEndDate.replace("Z", "")
        );
      }

      await DepositClaimService.getBySOCode(this.salesOrderData.Code).then(
        async (res) => {
          if (res.data.value.length > 0) {
            var data = res.data.value[0];
            this.headerFormModel.OrderAmount = data.OrderTotal;
            this.headerFormModel.OrderBalance = data.OrderBalance;
            this.headerFormModel.ClaimTotal = data.ClaimTotal;

            await DepositClaimService.getTermDueDate(
              new Date().toSaveDate(),
              data.CustomerTermCalculation,
              data.CustomerTerms
            ).then((rslt) => {
              this.TermDueDate = rslt.data;
            });
          }
        }
      );

      await DepositClaimService.getDeposiClaimDetails(
        this.salesOrderData.Code
      ).then((res) => {
        if (res.data.value.length > 0) {
          res.data.value.forEach((element, index) => {
            var obj = {
              ID: index + 1,
              ClaimDate: new Date(element.DateIn),
              DueDate: new Date(element.DateDue),
              DateSettled: element.DateSettled,
              Invoice: element.InvoiceNo,
              ClaimPrcnt: element.ClaimPcnt,
              FxClaimAmount: element.ClaimAmount,
              TaxFXLineTotal: element.TaxFXLineTotal
                ? element.TaxFXLineTotal
                : 0,
              Comment: element.Comment,
              IsNew: false,
              BankFeeBranchCode: null,
              BankFeeDivisionCode: null,
              BankFeeComment: null,
              BankFeeGLAccountCode: 0,
              PaymentMethod: null,
              BankAccount: null,
              ReferenceNumber: null,
              PaymentToggle: element.FXPaymentAmount > 0 ? true : false,
              FXPaymentAmount: element.FXPaymentAmount,
              FXAmount: 0,
            };
            this.localDataSource.insert(obj);
          });
        }
      });

      await BankFeeSettings.getDefaultSettings().then((res) => {
        this.BankFeeGLAccountCode = res.GLAccount;
      });

      await BankAccountTransactionServices.getPaymentMethods().then((res) => {
        this.DefaultPaymentMethod = res.data.value.filter(
          (x) => x.isDefault === 1
        )[0].PayType;
      });
      this.localDataSource.insert(this.getNewRowData());

      this.showPopupLoading(false);
      this.loading = false;
    },
    getNewRowData() {
      var row = { ...rowBase, PaymentMethod: this.DefaultPaymentMethod };
      const maxID = Math.max.apply(
        Math,
        this.localDataSource._array.map(function(o) {
          return o.ID;
        })
      );

      row.DueDate = this.TermDueDate;
      if (maxID != -Infinity) row.ID = maxID + 1;

      row.BankFeeGLAccountCode = this.BankFeeGLAccountCode;

      row.BankFeeBranchCode = getFactorByCode(666).Factorc;
      row.BankFeeDivisionCode = getFactorByCode(667).Factorc;
      row.BankFeeComment = getFactorByCode(668).Factorc;
      row.BankAccount = `${getFactorByCode(237).FactorInt}`;

      return row;
    },
    getSecondaryText(data) {
      let globalField = { ...getGlobalField(data.screenField) };

      return globalField.editorTemplate.secondary
        ? data[globalField.editorTemplate.secondary[0].dataField]
        : undefined;
    },
    getPrimaryText(data) {
      let globalField = { ...getGlobalField(data.screenField) };

      return data[globalField.editorTemplate.primary[0].dataField];
    },
    async saveDepositClaim() {
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;

      this.showPopupLoading(true);
      dataGrid.saveEditData().then(async () => {
      if (this.totalClaimAmount() > this.totalOrderAmount()) {
        this.showPopupLoading(false);
        let totalClaimAmount = this.totalClaimAmount().toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        });
        let totalOrderAmount = this.totalOrderAmount().toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        });
        this.displayValidationSummary([
          {
            message: `The total Claim Amount cannot be greater than the Order Amount. The total Claimed Amount is ${totalClaimAmount}. The total Order Amount is ${totalOrderAmount}.`,
          },
        ]);
        return;
      }


      await DepositClaimService.saveDeposiClaim(this.getSavePayload())
        .then((res) => {
          this.showPopupLoading(false);
          if (res) this.callBack(true);
        })
        .catch((err) => {
          if (err) this.showPopupLoading(false);
        });
      }).catch(() => {
          this.showPopupLoading(false);
        });
    },

    getSavePayload() {
      var payload = {
        SalesOrderNo: this.salesOrderData.Code,
        DepositClaimDetails: [],
      };

      this.localDataSource._array.forEach((element) => {
        if (element.IsNew && element.ClaimPrcnt && element.FxClaimAmount) {
          var maxLineNo = 0;
          if (!element.LineNo && payload.DepositClaimDetails.length > 0) {
            maxLineNo = Math.max.apply(
              Math,
              payload.DepositClaimDetails.map(function(o) {
                return o.LineNo;
              })
            );
            maxLineNo = maxLineNo + 1;
          } else if (element.LineNo && payload.DepositClaimDetails.length > 0) {
            maxLineNo = element.LineNo;
          }
          if (element.PaymentToggle) {
            payload.DepositClaimDetails.push({
              SalesOrderNo: this.salesOrderData.Code,
              LineNo: maxLineNo,
              ClaimAmount: element.FxClaimAmount,
              ClaimPcnt: element.ClaimPrcnt,
              Comment: element.Comment ? element.Comment : "",
              DateDue: new Date(element.DueDate).toSaveDate(),
              DateIn: new Date(element.ClaimDate).toSaveDate(),
              PayMethod: element.PaymentMethod,
              Reference: element.ReferenceNumber,
              BankAccountCode: element.BankAccount,
              AdjustmentFXAmount: element.FXAmount,
              AdjustmentBranchCode: element.BankFeeBranchCode,
              AdjustmentDivisionCode: element.BankFeeDivisionCode,
              AdjustmentGLAccountCode: element.BankFeeGLAccountCode,
              AdjustmentComment: element.BankFeeComment,
              TaxFXLineTotal: element.TaxFXLineTotal,
              FXPaymentAmount: element.FXPaymentAmount,
              ExchangeRate: this.headerFormModel.ExchangeRate,
            });
          } else {
            payload.DepositClaimDetails.push({
              SalesOrderNo: this.salesOrderData.Code,
              LineNo: maxLineNo,
              ClaimAmount: element.FxClaimAmount,
              ClaimPcnt: element.ClaimPrcnt,
              Comment: element.Comment ? element.Comment : "",
              DateDue: new Date(element.DueDate).toSaveDate(),
              DateIn: new Date(element.ClaimDate).toSaveDate(),
              PayMethod: null,
              Reference: null,
              BankAccountCode: null,
              AdjustmentFXAmount: 0,
              AdjustmentBranchCode: null,
              AdjustmentDivisionCode: null,
              AdjustmentGLAccountCode: null,
              AdjustmentComment: null,
              TaxFXLineTotal: element.TaxFXLineTotal,
              ExchangeRate: this.headerFormModel.ExchangeRate,
            });
          }
        }
      });

      return payload;
    },
    popupHidden() {
      this.callBack(false);
    },
    popupShown() {
      let popupContentArea = document.querySelector(
        ".gc-popup-deposit-claim .dx-overlay-content"
      );
      if (popupContentArea)
        this.popupContentHeight = popupContentArea.clientHeight;
    },
    async addNewRow(e) {
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;

      var row = this.localDataSource._array.find(
        (x) =>
          !x.ClaimPrcnt ||
          !x.FxClaimAmount ||
          !x.PaymentMethod ||
          !x.BankAccount
      );

      var validations = [];
      var validForm = true;
      if (row) {
        if (!row.ClaimPrcnt) {
          validations.push({
            message: "Claim % is required",
          });
        }
        if (!row.FxClaimAmount) {
          validations.push({
            message: "Claim Amount (FX) is required",
          });
        }

        if (
          row.IsNew == true &&
          ((row.PaymentMethod && !row.BankAccount) ||
            (!row.PaymentMethod && row.BankAccount))
        ) {
          dataGrid.expandRow(row).then(() => {
            validationEngine.validateGroup(
              `gc-popup-deposit-claim-header-form-${row.ID}`
            );
          });

          validForm = await validationEngine.validateGroup(
            `gc-popup-deposit-claim-header-form-${row.ID}`
          );

          if (validForm.isValid == false) {
            validForm.brokenRules.forEach((element) => {
              validations.push({
                message: element.message,
              });
            });
          }
        }
      }

      if (validations.length > 0) this.displayValidationSummary(validations);
      else {
        if (e.row.data.IsNew) dataGrid.collapseRow(e.row.data);
        this.localDataSource.insert(this.getNewRowData()).then((res) => {
          dataGrid.expandRow(res);
        });
      }
    },
    onRowExpanded(e) {
      setTimeout(() => {
        validationEngine.validateGroup(
          `gc-popup-deposit-claim-header-form-${e.key.ID}`
        );

        if (e.key.PaymentToggle == false) {
          this.localDataSource.update(e.key.ID, {
            PaymentToggle: true,
            FXPaymentAmount: e.key.FxClaimAmount + e.key.TaxFXLineTotal,
          });
        }
      }, 100);
    },
    onRowCollapsed(e) {
      if (
        !e.key.BankAccount ||
        !e.key.PaymentMethod ||
        e.key.FXPaymentAmount == 0
      )
        this.localDataSource.update(e.key.ID, { PaymentToggle: false });
    },
    onCellPrepared(e) {
      if (e.rowType === "data" && e.column.command === "expand") {
        if (e.data.IsNew == false) {
          e.cellElement.classList.remove("dx-datagrid-expand");
          e.cellElement.childNodes[0].classList.remove(
            "dx-datagrid-group-closed"
          );
        }
      }

      if (e.rowType === "data" && e.data.IsNew == false) {
        e.cellElement.classList.remove("gc-editable-cell-color");
      }
    },
    onEditorPreparing(e) {
      if (e.row.data.IsNew == false) {
        e.editorOptions.readOnly = true;
        e.editorOptions.onFocusIn = () => {};
      }
    },
    onHiddenGcPopupValidationSummary() {
      this.formsConfig.validationSummaryVisible = false;
    },
    onContentReadyDxMasterDetail(e, validationGroup) {
      setTimeout(() => {
        validationEngine.validateGroup(validationGroup);
      }, 100);
    },
    removeCellFocus() {
      var cell = document.querySelectorAll(".key-pad-focused-cell");
      if (cell.length > 0) cell[0].classList.remove("key-pad-focused-cell");
    },
  },
};
</script>

<style lang="scss">
.payment-toggle {
  float: right;
  position: absolute;
  right: 20px;
}

.add-payment-button {
  float: left;
}

.gc-popup-deposit-claim {
  .details-data-grid {
    padding: 0 10px 10px 10px;
    .gc-editable-cell-color[role="gridcell"]:not(.cell-disabled) {
      background-color: #f8fdfe !important;
      // #d1f7ffba !important
    }
    .dx-datagrid .dx-row.dx-data-row.dx-row-lines .key-pad-focused-cell {
      background-color: #d1f7ffba !important;
    }

    .dx-datagrid
      .dx-datagrid-content
      .dx-datagrid-table
      .dx-row
      .dx-editor-cell {
      padding-left: 5px !important;
      padding-right: 5px !important;
    }

    .dx-datagrid-rowsview .dx-master-detail-row > .dx-master-detail-cell {
      padding: 10px !important;
    }

    .master-details-form {
      padding: unset !important;

      .edit-fee {
        height: 100%;
        display: flex;
        align-items: flex-end;
        padding-left: unset !important;

        .edit-fee-template {
          color: #03a9f4;
          cursor: pointer;
        }
      }
      // background-color: white;

      // .dx-form {
      //   .dx-scrollable-container {
      //     margin-left: unset !important;
      //     padding-left: unset !important;
      //   }
      //   .dx-scrollable-content > .dx-layout-manager.dx-widget {
      //     padding-left: 12px;
      //     padding: 12px;
      //   }
      // }
    }
  }
  .footer {
    position: absolute;
    width: 100%;
    padding-top: 10px;
    bottom: 0;
    height: 45px;
    text-align: right;

    display: flex;
    flex-direction: row;
    justify-content: flex-end !important;

    .dx-button {
      display: flex;
      justify-content: center;
      flex-grow: 1;
      margin-right: 5px;
      flex-grow: unset !important;
    }
    .dx-button:last-child {
      margin-right: 0px;
    }
  }
}
</style>
